/**
 * Prepare a payment receipt for upload.
 *
 * Written after a real failure: a 4.06MB Android screenshot was rejected by
 * nginx with a 413 before the request ever reached the API, and the checkout
 * page could only say "That did not go through." Three things were wrong at
 * once, and all three bite ordinary students harder than they bit us:
 *
 *   1. Phone screenshots are 2-5MB. The proxy allowed 1MB.
 *   2. The server accepts only .jpg/.jpeg/.png/.pdf. An iPhone photo is HEIC
 *      and an Android screenshot is increasingly WEBP — both were refused by
 *      extension after a full upload had already been paid for over mobile data.
 *   3. A receipt is read by a human looking for an amount and a date. A 4MB
 *      original carries no more of that than a 300KB one, and on Ethiopian
 *      mobile data the difference is the whole experience.
 *
 * So: decode whatever the phone gives us, scale it down, and re-encode as
 * JPEG. That collapses all three problems into one answer — the file that
 * leaves the browser is small, and it is always a format the server accepts.
 *
 * Everything degrades rather than throws. If a browser cannot decode the
 * format (Chrome on Android still cannot read HEIC), we hand back the
 * original and let the server decide; a receipt that might work is better
 * than a refusal we invented on the client.
 */

/** The server's own cap (manual_payment_service.MAX_FILE_SIZE). */
export const SERVER_MAX_BYTES = 5 * 1024 * 1024;

/** Beyond this we stop before spending the student's data on an upload that
 *  cannot succeed. Well above any real screenshot. */
export const ABSURD_BYTES = 25 * 1024 * 1024;

/** Extensions the API will accept (ALLOWED_EXTENSIONS, server-side). */
const SERVER_OK = /\.(jpe?g|png|pdf)$/i;

/** Successive attempts, widest first. A receipt only has to stay legible. */
const ATTEMPTS: Array<{ edge: number; quality: number }> = [
  { edge: 1600, quality: 0.82 },
  { edge: 1400, quality: 0.75 },
  { edge: 1100, quality: 0.7 },
  { edge: 900, quality: 0.6 },
];

export type PreparedReceipt = {
  /** What to actually upload. */
  file: File;
  /** Object URL for the preview. The caller owns it and must revoke it. */
  previewUrl: string;
  isPdf: boolean;
  originalBytes: number;
  bytes: number;
};

export class ReceiptError extends Error {}

const bitmapFrom = async (file: File): Promise<ImageBitmap | HTMLImageElement> => {
  // `imageOrientation: 'from-image'` matters more than it looks: a photo taken
  // in portrait carries its rotation in EXIF, and a canvas that ignores it
  // uploads the receipt on its side.
  if (typeof createImageBitmap === 'function') {
    try {
      // Cast: this project's lib.dom still types ImageOrientation as
      // "none" | "flipY". "from-image" is supported by every browser we
      // serve and is what applies the EXIF rotation.
      return await createImageBitmap(file, {
        imageOrientation: 'from-image',
      } as unknown as ImageBitmapOptions);
    } catch {
      /* fall through to the <img> path */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('decode failed'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
};

const toBlob = (canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));

const humanSize = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export { humanSize };

export async function prepareReceipt(input: File): Promise<PreparedReceipt> {
  const originalBytes = input.size;

  if (originalBytes === 0) {
    throw new ReceiptError('That file is empty. Pick the photo again.');
  }
  if (originalBytes > ABSURD_BYTES) {
    throw new ReceiptError(
      `That file is ${humanSize(originalBytes)}. Send a photo or screenshot of the receipt instead.`
    );
  }

  const isPdf =
    input.type === 'application/pdf' || /\.pdf$/i.test(input.name || '');

  // A PDF is already the document; there is nothing sensible to re-encode.
  if (isPdf) {
    if (originalBytes > SERVER_MAX_BYTES) {
      throw new ReceiptError(
        `That PDF is ${humanSize(originalBytes)} and the limit is 5 MB. A screenshot of the receipt works too.`
      );
    }
    return {
      file: input,
      previewUrl: URL.createObjectURL(input),
      isPdf: true,
      originalBytes,
      bytes: originalBytes,
    };
  }

  const keepOriginal = (): PreparedReceipt => {
    // Only defensible if the server would actually take it.
    if (originalBytes > SERVER_MAX_BYTES || !SERVER_OK.test(input.name || '')) {
      throw new ReceiptError(
        "We couldn't read that image. Try a screenshot, or a JPG or PNG photo."
      );
    }
    return {
      file: input,
      previewUrl: URL.createObjectURL(input),
      isPdf: false,
      originalBytes,
      bytes: originalBytes,
    };
  };

  let source: ImageBitmap | HTMLImageElement;
  try {
    source = await bitmapFrom(input);
  } catch {
    return keepOriginal();
  }

  const width = (source as ImageBitmap).width || (source as HTMLImageElement).naturalWidth;
  const height = (source as ImageBitmap).height || (source as HTMLImageElement).naturalHeight;
  if (!width || !height) return keepOriginal();

  let best: Blob | null = null;
  for (const { edge, quality } of ATTEMPTS) {
    const scale = Math.min(1, edge / Math.max(width, height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));

    const ctx = canvas.getContext('2d');
    if (!ctx) break;
    // White behind the image: a transparent PNG flattened onto a JPEG's
    // default black makes dark receipt text unreadable.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(source as CanvasImageSource, 0, 0, canvas.width, canvas.height);

    // eslint-disable-next-line no-await-in-loop
    const blob = await toBlob(canvas, quality);
    if (!blob) break;
    best = blob;
    if (blob.size <= SERVER_MAX_BYTES) break;
  }

  if ('close' in source && typeof source.close === 'function') source.close();

  if (!best) return keepOriginal();
  if (best.size > SERVER_MAX_BYTES) {
    throw new ReceiptError(
      'That image is too large even after shrinking. Try a screenshot of the receipt.'
    );
  }

  // If the phone's own file was already smaller AND in a format the server
  // takes, there is no reason to send our re-encode instead.
  const useOriginal =
    originalBytes <= best.size &&
    originalBytes <= SERVER_MAX_BYTES &&
    SERVER_OK.test(input.name || '');

  const file = useOriginal
    ? input
    : new File([best], 'receipt.jpg', { type: 'image/jpeg' });

  return {
    file,
    previewUrl: URL.createObjectURL(file),
    isPdf: false,
    originalBytes,
    bytes: file.size,
  };
}
